/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { Button, Tooltip } from "@octopusdeploy/design-system-components";
import { UnavailableIcon } from "@octopusdeploy/design-system-icons";
import type { DashboardItemResource, ProjectResource, EnvironmentLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import { DashboardRenderMode, Repository, TaskState, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import type { Url } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import type { Dictionary } from "lodash";
import { isEqual, memoize } from "lodash";
import * as React from "react";
import type { GridCellRangeProps } from "react-virtualized";
import { CellMeasurer, CellMeasurerCache, defaultCellRangeRenderer } from "react-virtualized";
import { DeploymentTaskStatus } from "~/areas/dashboard/Deployments/DeploymentTaskStatus";
import { SampleProjectTourAbsoluteFullContainerHighlightStep } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTourStep";
import DashboardLimiter from "~/utils/DashboardLimiter/DashboardLimiter";
import type { DataCube } from "../DashboardDataSource/DataCube";
import { DimensionTypes } from "../DashboardDataSource/DataCube";
import type { DataSet, Group } from "../DashboardDataSource/DataSet";
import { getDataSetRows, getDataSetRowsByPage, getDataSet } from "../DashboardDataSource/DataSet";
import type { DeploymentOverviewFilters } from "../DeploymentsOverview/hooks/useDeploymentsOverviewFilters";
import { DashboardCell } from "./DashboardCell/DashboardCell";
import DashboardGrid from "./DashboardGrid";
import { ProjectDashboardEnvironmentColumnHeader } from "./ProjectDashboardEnvironmentColumnHeader";
import styles from "./style.module.less";
interface ProjectDashboardProps {
    spaceId: string;
    cube: DataCube;
    filters: DeploymentOverviewFilters;
    maximumRows?: number;
    allowDeployments?: boolean;
    dashboardRenderMode: DashboardRenderMode;
    project: ProjectResource;
    showLiveStatus: boolean;
}
interface ProjectDashboardState {
    height: number;
    groupTakeSizeLookup: Record<string, number>; // to minimise rendering for performance.
}
export type DeploymentContext = {
    environmentId: string;
    releaseId: string;
    tenantId: string;
    projectId: string;
};
const fixedCellWidth = 260;
// The designed height is 36/72 however due to the position of the scroll bar for this type of grid component we require some additional spacing to prevent the scroll bar blocking environment names
const defaultEnvironmentRowHeight = 36 + 16;
const environmentRowHeightWithDeployAllLink = 72 + 16;
export const ProjectDashboardLiveStatusGroupId = "live-status-group";
const ProjectDashboardLiveStatusRowIdLiveStatusRowId = "live-status-row";
export default class ProjectDashboard extends React.Component<ProjectDashboardProps, ProjectDashboardState> {
    private boundingDiv: HTMLElement | null = null;
    private cellMeasurerCaches: ((groupId: string, shouldShowChannelChips: boolean) => CellMeasurerCache) | null = null;
    private initialTop: number | null = null;
    private loadMoreTakeSize: number = Repository.takeDefaultPageSize;
    constructor(props: ProjectDashboardProps) {
        super(props);
        this.cellMeasurerCaches = memoize(this.getCellMeasurerCache);
        this.state = {
            height: 0,
            groupTakeSizeLookup: {},
        };
    }
    componentDidMount() {
        this.calculateTop();
        this.calculateHeight();
        window.addEventListener("resize", this.calculateHeight);
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.calculateHeight);
    }
    calculateTop() {
        if (this.boundingDiv !== null) {
            this.initialTop = this.boundingDiv.getBoundingClientRect().top;
        }
    }
    calculateHeight = () => {
        const approxHeightOfPaddingAndOtherJunk = 90;
        const height = window.innerHeight - this.initialTop! - approxHeightOfPaddingAndOtherJunk;
        this.setState({ height });
    };
    shouldComponentUpdate(nextProps: ProjectDashboardProps, nextState: ProjectDashboardState) {
        return !isEqual(getPropsForComparison(nextProps), getPropsForComparison(this.props)) || nextState.height !== this.state.height || nextState.groupTakeSizeLookup !== this.state.groupTakeSizeLookup;
        function getPropsForComparison(props: ProjectDashboardProps) {
            return props;
        }
    }
    render() {
        if (this.state.height === 0) {
            return <div ref={(div) => (this.boundingDiv = div)}/>;
        }
        const dataSet = getDataSet(this.props.filters, this.props.cube);
        const deploymentContext = this.getDeploymentContext(dataSet);
        const groups = this.props.project.TenantedDeploymentMode === TenantedDeploymentMode.Untenanted ? getDataSetRowsByPage(dataSet, this.props.filters.page, this.props.filters.pageSize) : getDataSetRows(dataSet);
        return this.redrawMatrix(dataSet, groups, deploymentContext);
    }
    private getCellMeasurerCache(groupId: string, shouldShowChips: boolean) {
        // Be careful about changing this width or height. Virtual scrolling requires a set width, but we need to accommodate
        // for a lot of customer scenarios. Too big and we annoy users who have small version numbers. Too small and we annoy
        // customers with long version numbers.
        // We need some extra height when chips are involved
        const height = shouldShowChips ? 100 : 70;
        return new CellMeasurerCache({
            defaultWidth: fixedCellWidth,
            minWidth: fixedCellWidth,
            fixedWidth: true,
            defaultHeight: height,
            minHeight: height,
        });
    }
    private getDeploymentContext(dataSet: DataSet): (groupId: string, rowId: string, columnId: string) => DeploymentContext {
        return (groupId: string, rowId: string, columnId: string) => ({
            environmentId: this.tryGetValue(dataSet, DimensionTypes.Environment)(groupId, rowId, columnId),
            releaseId: this.tryGetValue(dataSet, DimensionTypes.Release)(groupId, rowId, columnId),
            tenantId: this.tryGetValue(dataSet, DimensionTypes.Tenant)(groupId, rowId, columnId),
            projectId: this.tryGetValue(dataSet, DimensionTypes.Project)(groupId, rowId, columnId),
        });
    }
    private tryGetValue(dataSet: DataSet, dimensionType: DimensionTypes): (groupId: string, rowId: string, columnId: string) => string {
        const filters = this.props.filters;
        if (dataSet.groupDimension === dimensionType) {
            return (groupId, rowId, columnId) => groupId;
        }
        else if (dataSet.rowDimension === dimensionType) {
            return (groupId, rowId, columnId) => rowId;
        }
        else if (dataSet.columnDimension === dimensionType) {
            return (groupId, rowId, columnId) => columnId;
        }
        else if (filters[dimensionType]) {
            const environmentIds = Object.keys(filters[dimensionType]);
            if (environmentIds.length === 1) {
                return (groupId, rowId, columnId) => environmentIds[0];
            }
        }
        return () => null!;
    }
    private areAnyDeployToAllEnvironmentsLinksDisplayed(cube: DataCube, dataSet: DataSet): boolean {
        return (dataSet
            .getGroups()
            .flatMap((groupId) => dataSet.getColumnsForGroup(groupId).map((columnId) => this.tryGetDeployToAllEnvironmentsUrl(cube, dataSet, groupId, columnId)))
            .filter((url) => url !== null).length > 0);
    }
    private tryGetDeployToAllEnvironmentsUrl(cube: DataCube, dataSet: DataSet, groupId: string, columnId: string): Url | null {
        const environmentId = columnId;
        if (dataSet.columnDimension === DimensionTypes.Environment && dataSet.rowDimension === DimensionTypes.Tenant && this.props.filters[DimensionTypes.Release]) {
            const releaseFilters = Object.keys(this.props.filters[DimensionTypes.Release]);
            if (releaseFilters.length === 1) {
                const releaseId = Object.keys(this.props.filters[DimensionTypes.Release])[0];
                const projectId = Object.keys(this.props.filters[DimensionTypes.Project])[0];
                const groupTenantsWithoutSuccess = dataSet
                    .getRowsForGroup(groupId)
                    .filter((tenantId) => tenantId !== null &&
                    !cube.tenantIndex[tenantId].IsDisabled &&
                    cube.tenantIndex[tenantId].ProjectEnvironments[projectId].indexOf(environmentId) !== -1 &&
                    (!dataSet.matrix[groupId] || !dataSet.matrix[groupId][tenantId] || !dataSet.matrix[groupId][tenantId][environmentId] || !dataSet.matrix[groupId][tenantId][environmentId].find((item) => item.State === TaskState.Success)));
                const availableDeployments = (cube.nextAvailableDeployments[releaseFilters[0]] || {})[environmentId];
                if (availableDeployments) {
                    const canDeployAll = availableDeployments.filter((t) => groupTenantsWithoutSuccess.indexOf(t) !== -1);
                    if (canDeployAll.length > 0) {
                        const release = cube.releaseIndex[releaseId];
                        const uri = links.createDeploymentPage.generateUrl({ spaceId: this.props.spaceId, projectSlug: cube.projectIndex[projectId].Slug, releaseVersion: release?.Version ?? releaseId }, { environmentIds: [environmentId], tenantIds: groupTenantsWithoutSuccess });
                        return uri;
                    }
                }
            }
        }
        return null;
    }
    private environmentIsFrozen = (cube: DataCube, dataSet: DataSet, columnId: string) => {
        return dataSet.columnDimension === DimensionTypes.Environment && cube.environmentIndex[columnId]?.IsFrozen;
    };
    private redrawMatrix(dataSet: DataSet, groups: Group[], deploymentContext: (groupId: string, rowId: string, columnId: string) => DeploymentContext) {
        if (!dataSet) {
            return null;
        }
        const rowAndColumnCountsPerGroup = groups.map((g) => {
            const groupId = g.groupId;
            return { row: g.rowsInGroup.length, column: dataSet.getColumnsForGroup(groupId).length };
        });
        const dashboardLimit = new DashboardLimiter(rowAndColumnCountsPerGroup);
        const cube = this.props.cube;
        const shouldRenderSectionHeader = dataSet.shouldRenderSectionHeader() || this.props.showLiveStatus;
        const showLiveStatusRow = Object.keys(cube.tenantIndex).length == 0 && this.props.showLiveStatus;
        const tenantsInCube = Object.values(cube.tenantIndex);
        const hasNonDefaultChannels = Object.keys(cube.channelIndex).length > 1;
        const hasReleases = Object.keys(cube.releaseIndex).length > 0;
        const projectEnvironmentTenants: Dictionary<Dictionary<string[]>> = {};
        // We don't want to clutter cells with channel chips if the UI is grouping things by channel already.
        const shouldShowChannelChips = groups && groups.length === 1 && dataSet.groupDimension !== DimensionTypes.Channel && hasNonDefaultChannels;
        tenantsInCube.forEach((tenant) => {
            Object.keys(tenant.ProjectEnvironments).forEach((projectId) => {
                tenant.ProjectEnvironments[projectId].forEach((environmentId) => {
                    if (!projectEnvironmentTenants[projectId]) {
                        projectEnvironmentTenants[projectId] = {};
                    }
                    if (!projectEnvironmentTenants[projectId][environmentId]) {
                        projectEnvironmentTenants[projectId][environmentId] = [];
                    }
                    projectEnvironmentTenants[projectId][environmentId].push(tenant.Id);
                });
            });
        });
        const grids = groups.map((g, index) => {
            const groupId = g.groupId;
            const rowsInGroup = g.rowsInGroup;
            const columnsInGroup = dataSet.getColumnsForGroup(groupId);
            if (rowsInGroup.length === 0) {
                return <div key={groupId}></div>;
            }
            const group = dataSet.matrix[groupId] || {};
            const firstColumns = rowsInGroup.map((rowId) => dataSet.rowTitle(rowId));
            const isGridHeaderAndIncludedInSampleProjectTour = (cells: CellMeasurer[]): boolean => {
                const headerRowIndex = 0;
                const excludedColumnIndexes = [0];
                return cells?.every((cell) => cell.props.rowIndex === headerRowIndex && !excludedColumnIndexes.includes(cell.props.columnIndex!));
            };
            const cellRangeRenderer = (props: GridCellRangeProps) => {
                const children = defaultCellRangeRenderer(props);
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const cells = children as CellMeasurer[];
                if (isGridHeaderAndIncludedInSampleProjectTour(cells)) {
                    children.push(<SampleProjectTourAbsoluteFullContainerHighlightStep key={"SampleProjectTourStepDeploymentProcessEnvironments"} stepId={"DeploymentProcessEnvironments"}/>);
                }
                return children;
            };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cellRenderer = ({ columnIndex, rowIndex, key, style, parent }: any) => {
                const rowId = rowsInGroup[rowIndex - 1];
                //React-virtualized does not apply the fixed dimension styles for us and we need them otherwise the non-fixed dimension (height) may be calculated incorrectly
                const styleWithWidth = { ...style, width: fixedCellWidth };
                // Top left cell
                if (columnIndex === 0 && rowIndex === 0) {
                    return (<CellMeasurer cache={this.cellMeasurerCaches!(groupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                            <div style={styleWithWidth} className={emotionStyles.environmentHeaderContainer}>
                                <ProjectDashboardEnvironmentColumnHeader key={groupId} name={dataSet.firstColumnTitle()} isFrozen={false}/>
                            </div>
                        </CellMeasurer>);
                }
                // Left most column
                if (columnIndex === 0) {
                    return (<CellMeasurer cache={this.cellMeasurerCaches!(groupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                            <div style={styleWithWidth} className={cn(styles.headerCell, rowIndex > 0 ? "" : styles.highlightColumn)} key={key}>
                                {firstColumns[rowIndex - 1]}
                            </div>
                        </CellMeasurer>);
                }
                const columnId = columnsInGroup[columnIndex - 1];
                const environmentIsFrozen = this.environmentIsFrozen(this.props.cube, dataSet, columnId);
                // Cannot deploy all if the environment is frozen
                const deployAllUrl = !environmentIsFrozen && this.tryGetDeployToAllEnvironmentsUrl(this.props.cube, dataSet, groupId, columnId);
                // Top row
                if (rowIndex === 0) {
                    return (<CellMeasurer cache={this.cellMeasurerCaches!(groupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                            <div style={styleWithWidth} key={key}>
                                <ProjectDashboardEnvironmentColumnHeader key={columnId} name={dataSet.columnTitle(columnId)} isFrozen={environmentIsFrozen}/>
                                {deployAllUrl && (<Tooltip content={"Prepare and preview a deployment to this environment"}>
                                        <Button label="Deploy all..." href={deployAllUrl} importance="quiet"/>
                                    </Tooltip>)}
                            </div>
                        </CellMeasurer>);
                }
                const context = deploymentContext(groupId, rowId, columnId);
                const row = group[rowId] || {};
                const deployments = row[columnId];
                const deployment = this.getDeployment(deployments);
                const projectEnvironmentTenantIds = projectEnvironmentTenants[context.projectId] && projectEnvironmentTenants[context.projectId][context.environmentId] ? projectEnvironmentTenants[context.projectId][context.environmentId] : [];
                const liveStatus = this.props.showLiveStatus && !showLiveStatusRow ? this.tryGetLiveStatus(rowId, columnId) : undefined;
                return (<CellMeasurer cache={this.cellMeasurerCaches!(groupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                        <div style={styleWithWidth} className={styles.centerCell} key={key}>
                            <DashboardCell spaceId={this.props.spaceId} deployment={deployment} deployments={deployments} deploymentContext={context} allowDeployments={this.props.allowDeployments!} tenants={tenantsInCube} showChannelChips={shouldShowChannelChips} channelName={deployment ? this.getChannelName(deployment.ChannelId)! : null!} hasReleases={hasReleases} environment={cube.environmentIndex[context.environmentId]} project={cube.projectIndex[context.projectId]} nextAvailableDeploymentEnvironments={cube.nextAvailableDeployments[context.releaseId]} releaseVersion={context ? this.getReleaseVersion(context.releaseId)! : null!} projectEnvironmentTenantIds={projectEnvironmentTenantIds} liveStatus={liveStatus}/>
                        </div>
                    </CellMeasurer>);
            };
            const rowAndColumnLimit = dashboardLimit.getLimit(index);
            const isLimited = rowAndColumnLimit.row < rowsInGroup.length || rowAndColumnLimit.column < columnsInGroup.length;
            // We currently do not support paging when the dashboard is grouped by tag set
            const isProjectTenantedAndDashboardGrouped = this.props.project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted && this.props.filters.groupBy;
            if (isProjectTenantedAndDashboardGrouped) {
                const takeNumberOfRows = this.state.groupTakeSizeLookup[groupId] ? this.state.groupTakeSizeLookup[groupId] : this.loadMoreTakeSize;
                rowAndColumnLimit.row = takeNumberOfRows > rowAndColumnLimit.row ? rowAndColumnLimit.row : takeNumberOfRows;
            }
            const environmentRowHeight = this.areAnyDeployToAllEnvironmentsLinksDisplayed(this.props.cube, dataSet) ? environmentRowHeightWithDeployAllLink : defaultEnvironmentRowHeight;
            return (<div key={groupId}>
                    {shouldRenderSectionHeader && dataSet.groupTitle(groupId, this.props.showLiveStatus)}
                    <DashboardGrid rowCount={rowAndColumnLimit!.row + 1} columnCount={rowAndColumnLimit!.column + 1} cellRenderer={cellRenderer!} cellRangeRenderer={cellRangeRenderer!} cube={this.props.cube} cellMeasurerCache={this.cellMeasurerCaches!(groupId, shouldShowChannelChips)!} availableHeight={this.state.height} firstRowHeight={environmentRowHeight} showCapDataCallout={isLimited!} dashboardRenderMode={this.props.dashboardRenderMode}/>
                </div>);
        });
        return (<>
                {showLiveStatusRow && this.liveStatusGrid(dataSet, shouldRenderSectionHeader, shouldShowChannelChips, hasNonDefaultChannels)}
                {grids}
            </>);
    }
    private liveStatusGrid(dataSet: DataSet, shouldRenderSectionHeader: boolean, shouldShowChannelChips: boolean, hasNonDefaultChannels: boolean) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const liveStatusCellRenderer = ({ columnIndex, rowIndex, key, style, parent }: any) => {
            //React-virtualized does not apply the fixed dimension styles for us and we need them otherwise the non-fixed dimension (height) may be calculated incorrectly
            const styleWithWidth = { ...style, width: fixedCellWidth };
            // Top left cell
            if (columnIndex === 0 && rowIndex === 0) {
                return (<CellMeasurer cache={this.cellMeasurerCaches!(ProjectDashboardLiveStatusGroupId, shouldShowChannelChips)!} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                        <div style={styleWithWidth}></div>
                    </CellMeasurer>);
            }
            // Left most column
            if (columnIndex === 0) {
                return (<CellMeasurer cache={this.cellMeasurerCaches!(ProjectDashboardLiveStatusGroupId, shouldShowChannelChips)!} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                        <div style={styleWithWidth}></div>
                    </CellMeasurer>);
            }
            const columnId = Object.keys(this.props.cube.environmentIndex)[columnIndex - 1];
            const environmentIsFrozen = this.environmentIsFrozen(this.props.cube, dataSet, columnId);
            // Top row
            if (rowIndex === 0) {
                return (<CellMeasurer cache={this.cellMeasurerCaches!(ProjectDashboardLiveStatusGroupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                        <div style={styleWithWidth} key={key}>
                            <ProjectDashboardEnvironmentColumnHeader key={columnId} name={dataSet.columnTitle(columnId)} isFrozen={environmentIsFrozen}/>
                        </div>
                    </CellMeasurer>);
            }
            const deployments = this.props.cube.deployments.filter((deployment) => deployment.EnvironmentId == columnId);
            const deployment = deployments.sort((a, b) => (a.StartTime > b.StartTime ? -1 : 1))[0] ?? {};
            const liveStatus = this.tryGetLiveStatus(ProjectDashboardLiveStatusRowIdLiveStatusRowId, columnId);
            return (<CellMeasurer cache={this.cellMeasurerCaches!(ProjectDashboardLiveStatusGroupId, shouldShowChannelChips)} columnIndex={columnIndex} key={key} parent={parent} rowIndex={rowIndex}>
                    <div style={styleWithWidth} className={styles.centerCell} key={key}>
                        {liveStatus === undefined ? (<Tooltip content={"No live status data available for this environment"}>
                                <div className={emotionStyles.liveStatusUnavailableContainer}>
                                    <UnavailableIcon size={24}/>
                                </div>
                            </Tooltip>) : (<div className={cn(emotionStyles.liveStatusContainer)}>
                                <DeploymentTaskStatus spaceId={this.props.spaceId} projectSlug={this.props.project.Slug} deployment={deployment} isCollapsed={false} liveStatus={liveStatus} channelName={hasNonDefaultChannels && shouldShowChannelChips && deployment ? this.getChannelName(deployment.ChannelId)! : undefined} tenantProgress={"hidden"}/>
                            </div>)}
                    </div>
                </CellMeasurer>);
        };
        return (<>
                {shouldRenderSectionHeader && dataSet.groupTitle(ProjectDashboardLiveStatusGroupId, true)}
                <DashboardGrid key={ProjectDashboardLiveStatusGroupId} rowCount={2} columnCount={Object.keys(this.props.cube.environmentIndex).length + 1} cellRenderer={liveStatusCellRenderer!} cellRangeRenderer={defaultCellRangeRenderer!} cube={this.props.cube} cellMeasurerCache={this.cellMeasurerCaches!(ProjectDashboardLiveStatusGroupId, shouldShowChannelChips)!} availableHeight={this.state.height} showCapDataCallout={false} dashboardRenderMode={DashboardRenderMode.VirtualizeColumns} firstRowHeight={defaultEnvironmentRowHeight}/>
            </>);
    }
    // rowId may be the releaseId, tenantId, or some other row identifier
    private tryGetLiveStatus(rowId: string, environmentId: string): EnvironmentLiveStatusResource | undefined {
        const liveStatusForProject = this.props.cube.liveStatusIndex[this.props.project.Id];
        const environmentLiveStatuses = liveStatusForProject?.Environments?.filter((x) => x.Id == environmentId);
        // If rowId is a value tenant, then we know that this is for tenants
        const tenantLiveStatus = environmentLiveStatuses.find((x) => x.TenantId == rowId);
        if (tenantLiveStatus)
            return tenantLiveStatus;
        // Otherwise we just take the first environment live status, if it exists
        return environmentLiveStatuses.length > 0 ? environmentLiveStatuses[0] : undefined;
    }
    private getDeployment(deployments: DashboardItemResource[] | null) {
        if (!deployments || !deployments.length) {
            return null;
        }
        return deployments.find((item) => item.State === TaskState.Executing || item.State === TaskState.Failed || (item.State === TaskState.Success && item.HasWarningsOrErrors)) || deployments[0];
    }
    private getChannelName = (channelId: string): string | null => {
        const channel = this.props.cube.channelIndex[channelId];
        if (channel) {
            return channel.Name;
        }
        return null;
    };
    private getReleaseVersion = (releaseId: string): string | null => {
        const release = this.props.cube.releaseIndex[releaseId];
        if (release) {
            return release.Version;
        }
        return null;
    };
    static displayName = "ProjectDashboard";
}
const emotionStyles = {
    environmentHeaderContainer: css({ paddingLeft: 8 }),
    liveStatusContainer: css({
        display: "flex",
        padding: "0 3px",
    }),
    liveStatusUnavailableContainer: css({
        opacity: 0.2,
        fondSize: "2.5rem",
        paddingLeft: 9,
    }),
};
